import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { SecuritySettings } from '../types';

interface SecuritySectionProps {
  settings: SecuritySettings;
  onUpdate: (settings: SecuritySettings) => void;
  isAdmin: boolean;
}

const SecuritySection = ({ settings, onUpdate, isAdmin }: SecuritySectionProps) => {
  const [formData, setFormData] = useState(settings);
  const [hasChanges, setHasChanges] = useState(false);
  const [newIpAddress, setNewIpAddress] = useState('');

  const handleChange = (field: keyof SecuritySettings, value: any) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleAddIp = () => {
    if (newIpAddress && !formData.ipWhitelist.includes(newIpAddress)) {
      handleChange('ipWhitelist', [...formData.ipWhitelist, newIpAddress]);
      setNewIpAddress('');
    }
  };

  const handleRemoveIp = (ip: string) => {
    handleChange(
      'ipWhitelist',
      formData.ipWhitelist.filter((item) => item !== ip)
    );
  };

  const handleSave = () => {
    onUpdate(formData);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Configurações de Segurança</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie políticas de segurança e controle de acesso
          </p>
        </div>
        {hasChanges && isAdmin && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setFormData(settings)}>
              Descartar
            </Button>
            <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSave}>
              Salvar Configurações
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lock" size={20} color="var(--color-primary)" />
          Políticas de Autenticação
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Timeout de Sessão (minutos)"
            type="number"
            value={formData.sessionTimeout}
            onChange={(e) => handleChange('sessionTimeout', Number(e.target.value))}
            disabled={!isAdmin}
            min={5}
            max={480}
            description="Tempo de inatividade antes do logout automático"
          />

          <Input
            label="Expiração de Senha (dias)"
            type="number"
            value={formData.passwordExpiryDays}
            onChange={(e) => handleChange('passwordExpiryDays', Number(e.target.value))}
            disabled={!isAdmin}
            min={30}
            max={365}
            description="Dias até a senha precisar ser alterada"
          />

          <Input
            label="Máximo de Tentativas de Login"
            type="number"
            value={formData.maxLoginAttempts}
            onChange={(e) => handleChange('maxLoginAttempts', Number(e.target.value))}
            disabled={!isAdmin}
            min={3}
            max={10}
            description="Tentativas antes de bloquear a conta"
          />

          <div className="flex items-center">
            <Checkbox
              label="Autenticação de Dois Fatores"
              description="Exigir 2FA para todos os usuários"
              checked={formData.requireTwoFactor}
              onChange={(e) => handleChange('requireTwoFactor', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>

          <div className="flex items-center">
            <Checkbox
              label="Permitir Acesso Remoto"
              description="Habilitar login fora da rede corporativa"
              checked={formData.allowRemoteAccess}
              onChange={(e) => handleChange('allowRemoteAccess', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Shield" size={20} color="var(--color-primary)" />
          Lista de IPs Permitidos
        </h3>
        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                label="Adicionar Endereço IP"
                type="text"
                value={newIpAddress}
                onChange={(e) => setNewIpAddress(e.target.value)}
                placeholder="192.168.1.1"
                disabled={!isAdmin}
              />
            </div>
            <Button
              variant="outline"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddIp}
              disabled={!isAdmin || !newIpAddress}
            >
              Adicionar
            </Button>
          </div>

          <div className="space-y-2">
            {formData.ipWhitelist.length > 0 ? (
              formData.ipWhitelist.map((ip) => (
                <div
                  key={ip}
                  className="flex items-center justify-between p-3 bg-muted rounded-md"
                >
                  <div className="flex items-center gap-3">
                    <Icon name="Globe" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm font-mono text-foreground">{ip}</span>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleRemoveIp(ip)}
                      className="p-1 hover:bg-background rounded transition-colors"
                      aria-label="Remover IP"
                    >
                      <Icon name="Trash2" size={14} color="var(--color-destructive)" />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Icon
                  name="Globe"
                  size={48}
                  color="var(--color-muted-foreground)"
                  className="mx-auto mb-3"
                />
                <p className="text-muted-foreground">Nenhum IP na lista de permissões</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
          Atividade de Segurança Recente
        </h3>
        <div className="space-y-3">
          {[
            {
              type: 'success',
              icon: 'CheckCircle2',
              message: 'Login bem-sucedido de 192.168.1.100',
              time: '2 minutos atrás'
            },
            {
              type: 'warning',
              icon: 'AlertTriangle',
              message: 'Tentativa de login falhada de IP não autorizado',
              time: '15 minutos atrás'
            },
            {
              type: 'info',
              icon: 'Info',
              message: 'Senha alterada com sucesso',
              time: '1 hora atrás'
            },
            {
              type: 'success',
              icon: 'Shield',
              message: 'Autenticação de dois fatores ativada',
              time: '3 horas atrás'
            }
          ].map((activity, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-md">
              <Icon
                name={activity.icon}
                size={16}
                color={
                  activity.type === 'success' ?'var(--color-success)'
                    : activity.type === 'warning' ?'var(--color-warning)' :'var(--color-accent)'
                }
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="text-sm text-foreground">{activity.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {!isAdmin && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Acesso Restrito</p>
              <p className="text-sm text-muted-foreground mt-1">
                Apenas administradores podem modificar as configurações de segurança. Entre em
                contato com seu supervisor para alterações.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;